import { CurrentUser, $Constants as $AppConstants, Company } from 'app/classes';
import { IdentityService } from 'app/services/identity.service//identity.service';
import auth0 from "auth0-js";
import * as Cookie from 'browser-cookies';
import { apps, auth0Env, url, doesIdentitySayBrowserIsAuth0Capable } from "../../../main";
import { stringify} from "../../../classes";

const OPENED = 'opened';
const CLOSED = 'closed';

export class ProfileDropdownController implements ng.IController {

    public state;
    public user: CurrentUser;
    public isOpen: boolean = false;
    public orgOpen: boolean = false;
    public showOrgs: boolean = false;
    public showLogin: boolean = false;
    public showResults: boolean = true;
    public tokenSource: string;
    public password: string;
    public error: boolean = false;
    public selectedOrg: Company;

    public static $inject = [
        '$Constants',
        '$CurrentUser',
        '$scope',
        '$timeout',
        '$Router',
        '$window',
        'OrgSwitchConfirmModal',
        'DropdownManagerService',
        '$IdentityResource'
    ];

    constructor(
        private $Constants,
        private $CurrentUser,
        private $scope,
        private $timeout,
        private $Router,
        private $window,
        private OrgSwitchConfirmModal,
        private DropdownManagerService,
        private $IdentityResource) {

        const defaultUrl = $AppConstants.cdnAssets + 'images/user.gif';

        $CurrentUser.onLogin(() => {
            this.user = $CurrentUser;
            if (!this.user.profile.avatarImageUrl) {
                this.user.profile.avatarImageUrl = defaultUrl;
            }
            this.showOrgs = ($CurrentUser.companies.length > 1);
            this.selectedOrg = this.user.company;
            this.tokenSource =  $CurrentUser.tokenSource;
        }, true);

        $CurrentUser.onLogout(() => {
            this.showOrgs = false;
            delete this['user'];
        }, true);

        $scope.$watch('ctrl.state', (val, oldVal) => {
            if (val && val !== oldVal) {
                this.isOpen = (val === 'opened');
            }
        });

        $window.addEventListener('refreshAvatar', (event) => {
            let imageUrl;
            if (event.detail === 'default') {
                imageUrl = defaultUrl;
            } else {
                imageUrl = event.detail;
            }
            this.user.profile.avatarImageUrl = imageUrl;
            $timeout();
        });

        // Event listner for input change event which will be
        // triggered by the angucomplete directive/component
        $window.addEventListener('anguCompleteinputChanged', (e) => {
            if (e.detail.length > 0) {
                this.showResults = false;
            } else {
                this.showResults = true;
            }
        });
    }

    // Callback triggered when autocomplete input is changed
    // Since the function is executed within the scope of the angucomplete directive,
    // we cannot access the ProfileDropdownController's scope so we are forced to
    // emit a window event
    public inputChanged(input) {
        const evt = new CustomEvent('anguCompleteinputChanged', { detail: input });
        window.dispatchEvent(evt);
    }

    /**
     * 
     * @param orgObject {object} - the org to be switched too; should contain org id
     * @param tokenSource {string} - 'auth0' | 'identity'
     * 100% side-effect to trigger function to display password (identity login)
     * or immediately initiate silent org switch with Auth0
     */
    public triggerLogin(orgObject, tokenSource) {
        if(tokenSource === 'identity') {
            this.viewLogin(orgObject);
        } else {
            this.loginWithAuth0(orgObject.id);
        }
    }

    // On click callback from autocomplete component results when user is part of more than 5 orgs
    public switchOrgAutoComplete(org, scope) {
        scope.orgOpen = false;
        scope.triggerLogin(org.originalObject, scope.tokenSource);
    }

    // On click callback for static list when user is part of 5 or less orgs
    public switchOrgStandard(org) {
        this.orgOpen = false;
        this.triggerLogin(org, this.tokenSource);
    }

    public loginWithAuth0(orgId) {
        localStorage.setItem('triedToSwitchToOrgId', orgId);
        Cookie.erase('token');
        // Create an instance of auth0-js using the correct environment
        const auth = new auth0.WebAuth({ 
            ...apps[auth0Env.internal],
            audience: "api://api.spscommerce.com/",
            responseType: "token",
            scope: 'openid profile email', 
        });
        // fire le redirect
        auth.authorize({
            realm: apps[auth0Env.internal].realm,
            appState: stringify({
              initialPath: '/launchpad/'
            }),
            login_hint: orgId,
            redirectUri : url + '/auth0/callback',
            prompt: 'none'
        });
    }

    public open() {
        this.isOpen = true;
        this.state = OPENED;
    }

    public close() {
        this.isOpen = false;
        this.state = CLOSED;
    }

    public toggle($event: Event) {
        $event.stopPropagation();
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    public organizationToggle() {
        this.closeLogin();
        this.orgOpen = !this.orgOpen;
    }

    public viewLogin(org: Company) {
        this.selectedOrg = org;
        this.error = false;
        this.password = '';
        // we do not want to trigger the login state if the user reselects the org
        // that they are currently logged into
        if (org.id !== this.user.company.id) {
            this.showLogin = true;
        }
    }

    public closeLogin() {
        this.showLogin = false;
    }

    public login(orgId) {
        const payload = {
            'grant_type': 'change_active_organization',
            'password': this.password,
            'organization_id': orgId
        };
        // set error to false in case this is not first confirm
        // and need to redisplay error message.
        this.error = false;
        this.$IdentityResource.post('token/', [], payload)
            .then((res) => {
                this.$CurrentUser.setToken(res.access_token);
                // load new route
                this.$Router.goto('/launchpad/');
                // reload to refresh blue bar
                window.location.reload();
            })
            .catch((err) => {
                this.error = true;
                this.$scope.$evalAsync();
            });
    }

    public switchOrg(orgId) {
        const {identityPrefix} = auth0Env;
        doesIdentitySayBrowserIsAuth0Capable({organizations: [orgId], identityPrefix}).then((result) => {
            if(result) {
                Cookie.erase('token');
                this.$CurrentUser.destroySession({
                    silent: false, 
                    switchToOrgId: orgId,
                    switchUsingAuth0: true
                });
                return;
            }
            this.login(orgId);
        }).catch(e => {
            this.login(orgId);
        })
    }

    public logout() {
        this.$CurrentUser.destroySession();
        this.close();
    }

    public viewProfile() {
        this.$Router.goto('/launchpad/profile/edit/');
        this.close();
    }

    public viewSettings() {
        this.$Router.goto('/launchpad/settings/');
        this.close();
    }
}
